import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { APIS } from '../../../../../utils/api-factory';
import {
  getUserChatConversationApi,
  markAllMessagesAsReadApi,
  markMessageAsReadApi,
  sendUserChatMessageApi
} from '../../AdminActionCreator.component';
import moment from 'moment';
import {
  Send,
  Search,
  MessageSquare,
  RefreshCw,
  Check,
  CheckCheck,
  FileText,
  Download
} from 'lucide-react';

// Lightweight, self-contained STOMP-over-WebSocket client
class SimpleStompClient {
  constructor(url, onConnect, onMessage, onError, onDisconnect, onConnecting) {
    this.url = url;
    this.onConnect = onConnect;
    this.onMessage = onMessage;
    this.onError = onError;
    this.onDisconnect = onDisconnect;
    this.onConnecting = onConnecting;
    this.ws = null;
    this.connected = false;
    this.subscriptions = new Map();
    this.connectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.isExplicitDisconnect = false;
  }

  connect() {
    this.isExplicitDisconnect = false;
    console.log(`STOMP: Connecting to WebSocket URL: ${this.url}`);
    if (this.onConnecting) {
      this.onConnecting();
    }
    try {
      this.ws = new WebSocket(this.url);
    } catch (e) {
      console.error("STOMP: WebSocket instantiation failed", e);
      if (this.onError) this.onError(e);
      this.handleReconnect();
      return;
    }

    this.ws.onopen = () => {
      console.log("STOMP: Connection established. Sending CONNECT frame.");
      this.sendFrame('CONNECT', {
        'accept-version': '1.1,1.2',
        'heart-beat': '10000,10000'
      });
    };

    this.ws.onmessage = (event) => {
      const rawData = event.data;
      // Heartbeat management
      if (rawData === '\n' || rawData === '\r\n') {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send('\n'); // Pong heartbeat
        }
        return;
      }

      const frame = this.parseFrame(rawData);
      if (!frame) return;

      console.log(`STOMP: Frame processed [${frame.command}]`);
      if (frame.command === 'CONNECTED') {
        this.connected = true;
        this.connectAttempts = 0;
        if (this.onConnect) this.onConnect();
        
        // Restore active subscriptions on reconnect
        for (const [dest, subId] of this.subscriptions.entries()) {
          this.subscribe(dest, subId);
        }
      } else if (frame.command === 'MESSAGE') {
        if (this.onMessage) this.onMessage(frame);
      } else if (frame.command === 'ERROR') {
        console.error("STOMP: Received error frame", frame);
        if (this.onError) this.onError(frame);
      }
    };

    this.ws.onerror = (error) => {
      console.error("STOMP: WebSocket transport error", error);
      if (this.onError) this.onError(error);
    };

    this.ws.onclose = (event) => {
      console.log("STOMP: WebSocket connection closed", event);
      this.connected = false;
      if (this.onDisconnect) this.onDisconnect();
      if (!this.isExplicitDisconnect) {
        this.handleReconnect();
      }
    };
  }

  handleReconnect() {
    if (this.connectAttempts < this.maxReconnectAttempts) {
      this.connectAttempts++;
      if (this.onConnecting) {
        this.onConnecting();
      }
      const delay = Math.min(1000 * Math.pow(2, this.connectAttempts), 8000);
      console.log(`STOMP: Reconnect attempt ${this.connectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);
      setTimeout(() => {
        // Fallback: If connection using direct '/ws' fails repeatedly, attempt with '/ws/websocket' SockJS fallback path
        if (this.connectAttempts === 2 && this.url.endsWith('/ws')) {
          this.url = this.url + '/websocket';
        }
        this.connect();
      }, delay);
    } else {
      console.error("STOMP: Maximum reconnection attempts reached. Halting auto-reconnect.");
    }
  }

  subscribe(destination, id = 'sub-' + Math.random().toString(36).substr(2, 9)) {
    this.subscriptions.set(destination, id);
    if (this.connected && this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log(`STOMP: Subscribing to destination: ${destination}`);
      this.sendFrame('SUBSCRIBE', { id, destination });
    }
  }

  unsubscribe(destination) {
    const id = this.subscriptions.get(destination);
    if (id) {
      this.subscriptions.delete(destination);
      if (this.connected && this.ws && this.ws.readyState === WebSocket.OPEN) {
        console.log(`STOMP: Unsubscribing from destination: ${destination}`);
        this.sendFrame('UNSUBSCRIBE', { id });
      }
    }
  }

  send(destination, headers = {}, body = '') {
    if (this.connected && this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.sendFrame('SEND', { ...headers, destination }, body);
    } else {
      console.warn("STOMP: Failed to transmit message. Connection is offline.");
    }
  }

  sendFrame(command, headers = {}, body = '') {
    let frame = command + '\n';
    for (const [key, value] of Object.entries(headers)) {
      frame += `${key}:${value}\n`;
    }
    frame += '\n' + body + '\u0000';
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(frame);
    }
  }

  parseFrame(rawData) {
    const trimmed = rawData.replace(/^[\r\n]+/, '');
    if (!trimmed) return null;

    const nullIdx = trimmed.indexOf('\u0000');
    const content = nullIdx >= 0 ? trimmed.substring(0, nullIdx) : trimmed;

    const [headerPart, ...bodyParts] = content.split('\n\n');
    const body = bodyParts.join('\n\n');

    const lines = headerPart.split('\n');
    const command = lines[0].trim();
    const headers = {};

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (line) {
        const colonIdx = line.indexOf(':');
        if (colonIdx >= 0) {
          const key = line.substring(0, colonIdx).trim();
          const val = line.substring(colonIdx + 1).trim();
          headers[key] = val;
        }
      }
    }

    return { command, headers, body };
  }

  disconnect() {
    console.log("STOMP: Disconnecting client connection.");
    this.isExplicitDisconnect = true;
    this.connectAttempts = 100;
    if (this.ws) {
      this.ws.close();
    }
  }
}

export default function Chat({ admin, allDetail }) {
  const userId = allDetail?.userId;
  const adminEmail = admin?.emailId;
  const customerAppId = allDetail?.applicationId;
  const firstName = allDetail?.firstName;
  const lastName = allDetail?.lastName;
  const customerMobile = allDetail?.mobileNumber;
  const customerEmail = allDetail?.emailId;

  const [messages, setMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('connecting'); // 'connecting' | 'connected' | 'disconnected'
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const messagesEndRef = useRef(null);
  const stompClientRef = useRef(null);
  const textareaRef = useRef(null);

  const handleDownload = (e, url, fileName) => {
    e.preventDefault();
    
    // Correct extension mismatch between filename and type parameter
    const isPdf = /type=pdf/i.test(url);
    let finalFileName = fileName;
    if (isPdf) {
      if (/\.(jpe?g|png|gif|webp)$/i.test(finalFileName)) {
        finalFileName = finalFileName.replace(/\.(jpe?g|png|gif|webp)$/i, '.pdf');
      } else if (!finalFileName.toLowerCase().endsWith('.pdf')) {
        finalFileName += '.pdf';
      }
    } else {
      if (finalFileName.toLowerCase().endsWith('.pdf')) {
        finalFileName = finalFileName.replace(/\.pdf$/i, '.jpg');
      }
    }

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error('Network response was not ok');
        const contentType = res.headers.get('content-type');
        if (contentType && (contentType.includes('text/html') || contentType.includes('application/json'))) {
          throw new Error('Response is HTML/JSON page, not a binary file');
        }
        return res.blob();
      })
      .then((blob) => {
        const localUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = localUrl;
        a.download = finalFileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(localUrl);
        a.remove();
      })
      .catch((err) => {
        console.error("Secure download failed, falling back to direct link:", err);
        window.open(url, '_blank');
      });
  };

  const linkifyText = (text) => {
    if (typeof text !== 'string') return text;
    const urlRegex = /(https?:\/\/[^\s]+)/gi;
    const parts = text.split(urlRegex);
    if (parts.length === 1) return text;
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const renderMessageContent = (comment) => {
    if (typeof comment === 'string' && comment.includes('downloadpdf')) {
      const parts = comment.split('|');
      const downloadUrl = parts[0].trim();
      const rawFileName = parts[1] ? parts[1].trim() : 'Document';
      const isPdf = /type=pdf/i.test(downloadUrl);

      // Sanitize displayed/downloaded filename
      let fileName = rawFileName;
      if (isPdf) {
        if (/\.(jpe?g|png|gif|webp)$/i.test(fileName)) {
          fileName = fileName.replace(/\.(jpe?g|png|gif|webp)$/i, '.pdf');
        } else if (!fileName.toLowerCase().endsWith('.pdf')) {
          fileName += '.pdf';
        }
      } else {
        if (fileName.toLowerCase().endsWith('.pdf')) {
          fileName = fileName.replace(/\.pdf$/i, '.jpg');
        }
      }

      if (isPdf) {
        return (
          <div className="pdf-message-card">
            <div className="pdf-icon-wrapper">
              <FileText size={20} />
            </div>
            <div className="pdf-details">
              <span className="pdf-name" title={fileName}>{fileName}</span>
              <span className="pdf-type">PDF Document</span>
            </div>
            <a 
              href={downloadUrl} 
              download={fileName} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="pdf-download-btn"
              title="Download PDF"
              onClick={(e) => handleDownload(e, downloadUrl, fileName)}
            >
              <Download size={14} />
            </a>
          </div>
        );
      } else {
        return (
          <div className="image-message-card">
            <img 
              src={downloadUrl} 
              alt={fileName} 
              className="chat-image-preview"
              onClick={() => window.open(downloadUrl, '_blank')}
            />
            <div className="image-footer">
              <span className="image-name" title={fileName}>{fileName}</span>
              <a 
                href={downloadUrl} 
                download={fileName} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="image-download-btn"
                title="Download Image"
                onClick={(e) => handleDownload(e, downloadUrl, fileName)}
              >
                <Download size={14} />
              </a>
            </div>
          </div>
        );
      }
    }
    return linkifyText(comment);
  };

  // REST API: Load complete conversation thread
  const loadConversation = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    getUserChatConversationApi(userId,admin?.emailId, (data) => {
      if (data && Array.isArray(data)) {
        // Sort conversation chronologically
        const sorted = data.sort((a, b) => new Date(a.addedOn) - new Date(b.addedOn));
        setMessages(sorted);
      } else {
        console.error('Failed to retrieve chat history from server');
      }
      setLoading(false);
    });
  }, [userId]);

  // REST API: Quietly refresh conversation thread (to update read receipts/blue ticks)
  const refreshConversation = useCallback(() => {
    if (!userId) return;
    getUserChatConversationApi(userId,admin.emailId, (data) => {
      if (data && Array.isArray(data)) {
        const sorted = data.sort((a, b) => new Date(a.addedOn) - new Date(b.addedOn));
        setMessages((prev) => {
          // Compare prev and sorted. If read status, comment, or message count differs, update.
          const hasDifferences = prev.length !== sorted.length || prev.some((m, idx) => {
            const sm = sorted[idx];
            if (!sm) return true;
            return m.messageId !== sm.messageId || 
                   (m.isRead || m.read) !== (sm.isRead || sm.read) ||
                   m.comment !== sm.comment;
          });
          if (hasDifferences) {
            return sorted;
          }
          return prev;
        });
      }
    });
  }, [userId]);

  // Set up polling interval to fetch updated read status in background
  useEffect(() => {
    if (!userId) return;
    const intervalId = setInterval(() => {
      refreshConversation();
    }, 10000); // 10 seconds interval
    return () => clearInterval(intervalId);
  }, [userId, refreshConversation]);

  // REST API: Mark all messages for this user as read
  const clearUnreadStatus = useCallback(() => {
    if (!userId) return;
    markAllMessagesAsReadApi(userId, (res) => {
      console.log('Unread status cleared:', res);
    });
  }, [userId]);

  // REST API: Mark a single message as read
  const markMessageAsRead = useCallback((messageId) => {
    markMessageAsReadApi(messageId, (res) => {
      console.log(`Message ${messageId} marked as read:`, res);
    });
  }, []);

  // REST API: Send Admin reply message
  const sendMessage = useCallback((text) => {
    if (!text.trim() || !userId || !adminEmail) return;
    setSending(true);
    const payload = {
      senderId: adminEmail,
      receiverId: userId,
      comment: text,
      senderType: 'ADMIN'
    };
    sendUserChatMessageApi(payload, (newMsg) => {
      let resolvedMsg = newMsg?.data || newMsg;
      if (typeof newMsg === 'string' || !resolvedMsg || !resolvedMsg.messageId) {
        // Fallback if server returns raw string response instead of JSON object
        resolvedMsg = {
          messageId: 'temp-' + Date.now(),
          senderId: adminEmail,
          receiverId: userId,
          comment: text,
          senderType: 'ADMIN',
          addedOn: new Date().toISOString(),
          isRead: false
        };
      }
      setMessages((prev) => {
        if (prev.some((m) => m.messageId === resolvedMsg.messageId)) return prev;
        return [...prev, resolvedMsg];
      });
      setInputValue('');
      if (textareaRef.current) {
        textareaRef.current.style.height = '36px';
        textareaRef.current.style.overflowY = 'hidden';
      }
      setSending(false);
    });
  }, [userId, adminEmail]);

  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  // Establish connection and clean up subscriptions
  useEffect(() => {
    if (!userId) return;
    
    loadConversation();
    clearUnreadStatus();

    let client = null;

    if (APIS.BASE_URL) {
      let wsUrl = APIS.BASE_URL.replace(/^http/, 'ws');
      if (wsUrl.endsWith('/')) {
        wsUrl = wsUrl.slice(0, -1);
      }
      wsUrl += '/ws';

      client = new SimpleStompClient(
        wsUrl,
        () => {
          setConnectionStatus('connected');
          client.subscribe('/topic/chat/ADMIN');
        },
        (frame) => {
          try {
            const msg = JSON.parse(frame.body);
            const isUserSender = msg.senderType === 'USER' && msg.senderId === userId;
            const isAdminSender = msg.senderType === 'ADMIN' && msg.receiverId === userId;
            
            if (isUserSender || isAdminSender) {
              setMessages((prev) => {
                const index = prev.findIndex((m) => m.messageId === msg.messageId);
                if (index !== -1) {
                  // Message exists, check if read status or comment changed
                  const existing = prev[index];
                  const existingRead = !!(existing.isRead || existing.read);
                  const newRead = !!(msg.isRead || msg.read);
                  if (existingRead !== newRead || existing.comment !== msg.comment) {
                    const updated = [...prev];
                    updated[index] = { ...existing, ...msg };
                    return updated;
                  }
                  return prev;
                }
                return [...prev, msg];
              });
              
              const isMsgRead = msg.isRead || msg.read;
              if (isUserSender && !isMsgRead) {
                markMessageAsRead(msg.messageId);
              }
            }
          } catch (e) {
            console.error('WebSocket payload parsing error:', e);
          }
        },
        (error) => {
          console.error('WebSocket client connection error:', error);
          setConnectionStatus('disconnected');
        },
        () => {
          setConnectionStatus('disconnected');
        },
        () => {
          setConnectionStatus('connecting');
        }
      );

      client.connect();
      stompClientRef.current = client;
    }

    return () => {
      if (client) {
        client.disconnect();
      }
    };
  }, [userId, loadConversation, clearUnreadStatus, markMessageAsRead]);

  // Handle auto-scroll to bottom of conversation feed
  useEffect(() => {
    scrollToBottom(loading ? 'auto' : 'smooth');
  }, [messages, loading]);

  // Memoized search-filtered conversation thread
  const filteredMessages = useMemo(() => {
    if (!searchQuery.trim()) return messages;
    const q = searchQuery.toLowerCase();
    return messages.filter(m => m.comment && m.comment.toLowerCase().includes(q));
  }, [messages, searchQuery]);

  const customerName = useMemo(() => {
    return `${firstName || ''} ${lastName || ''}`.trim() || 'Customer';
  }, [firstName, lastName]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 76; // Max height corresponding to approximately 3 rows (20px * 3 + 16px padding)
      
      if (val === '') {
        textareaRef.current.style.height = '36px';
        textareaRef.current.style.overflowY = 'hidden';
      } else {
        textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
        textareaRef.current.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
      }
    }
  };

  const cssStyles = `
    .admin-chat-layout {
      display: flex !important;
      flex-direction: column !important;
      height: 540px;
      background: #f8fafc !important;
      border: 1px solid #e2e8f0 !important;
      border-radius: 16px !important;
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05) !important;
      overflow: hidden !important;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
    }

    .chat-header {
      display: flex !important;
      justify-content: space-between !important;
      align-items: center !important;
      padding: 16px 24px !important;
      background: #ffffff !important;
      color: #0f172a !important;
      border-bottom: 1px solid #e2e8f0 !important;
    }

    .chat-header-info {
      display: flex !important;
      align-items: center !important;
      gap: 12px !important;
    }

    .chat-header-avatar {
      display: flex !important;
      justify-content: center !important;
      align-items: center !important;
      width: 42px !important;
      height: 42px !important;
      border-radius: 50% !important;
      background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
      font-weight: 600 !important;
      font-size: 16px !important;
      color: #ffffff !important;
      box-shadow: 0 4px 10px rgba(59, 130, 246, 0.25) !important;
      flex-shrink: 0 !important;
      line-height: 1 !important;
    }

    .chat-header-details h4 {
      margin: 0 !important;
      font-size: 16px !important;
      font-weight: 600 !important;
      color: #0f172a !important;
      line-height: 1.2 !important;
    }

    .chat-header-details p {
      margin: 4px 0 0 0 !important;
      font-size: 12px !important;
      color: #64748b !important;
      line-height: 1.2 !important;
      word-break: break-word !important;
      overflow-wrap: break-word !important;
    }

    .chat-header-status {
      display: flex !important;
      align-items: center !important;
      font-size: 13px !important;
      background: #f1f5f9 !important;
      color: #475569 !important;
      padding: 6px 14px !important;
      border-radius: 20px !important;
      border: 1px solid #e2e8f0 !important;
      font-weight: 500 !important;
      margin-right: 36px !important;
    }

    @keyframes pulse {
      0% { transform: scale(0.95); opacity: 0.8; }
      50% { transform: scale(1.15); opacity: 1; }
      100% { transform: scale(0.95); opacity: 0.8; }
    }

    .status-dot {
      width: 8px !important;
      height: 8px !important;
      border-radius: 50% !important;
      display: inline-block !important;
      margin-right: 8px !important;
      animation: pulse 2s infinite ease-in-out !important;
    }

    .status-dot.connected {
      background-color: #10b981 !important;
      box-shadow: 0 0 8px rgba(16, 185, 129, 0.4) !important;
    }

    .status-dot.connecting {
      background-color: #f59e0b !important;
      box-shadow: 0 0 8px rgba(245, 158, 11, 0.4) !important;
    }

    .status-dot.disconnected {
      background-color: #ef4444 !important;
      box-shadow: 0 0 8px rgba(239, 68, 68, 0.4) !important;
    }

    .chat-search-bar {
      display: flex !important;
      align-items: center !important;
      padding: 10px 24px !important;
      background: #ffffff !important;
      border-bottom: 1px solid #e2e8f0 !important;
      position: relative !important;
    }

    .chat-search-bar svg {
      position: absolute !important;
      left: 36px !important;
      color: #94a3b8 !important;
      width: auto !important;
      height: 16px !important;
    }

    .admin-chat-layout .chat-search-input {
      width: 100% !important;
      padding: 8px 12px 8px 36px !important;
      font-size: 14px !important;
      border: 1px solid #cbd5e1 !important;
      border-radius: 8px !important;
      background-color: #f8fafc !important;
      background: #f8fafc !important;
      outline: none !important;
      transition: all 0.2s ease !important;
      color: #0f172a !important;
      box-shadow: none !important;
    }

    .admin-chat-layout .chat-search-input:focus {
      border-color: #3b82f6 !important;
      background-color: #ffffff !important;
      background: #ffffff !important;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
    }

    .chat-messages-area {
      flex: 1 !important;
      overflow-y: auto !important;
      overflow-x: hidden !important;
      padding: 24px !important;
      background-color: #f8fafc !important;
      display: flex !important;
      flex-direction: column !important;
      gap: 16px !important;
    }

    /* Scrollbar */
    .chat-messages-area::-webkit-scrollbar {
      width: 6px !important;
    }
    .chat-messages-area::-webkit-scrollbar-track {
      background: transparent !important;
    }
    .chat-messages-area::-webkit-scrollbar-thumb {
      background: #cbd5e1 !important;
      border-radius: 4px !important;
    }

    .message-row {
      display: flex !important;
      width: 100% !important;
      animation: slideUp 0.3s ease-out !important;
    }

    @keyframes slideUp {
      from { transform: translateY(10px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }

    .message-row.admin {
      justify-content: flex-end !important;
      background:transparent;
    }

    .message-row.user {
      justify-content: flex-start !important;
      background:transparent;
    }

    .message-bubble-wrapper {
      max-width: 70% !important;
      display: flex !important;
      flex-direction: column !important;
    }

    .message-row.admin .message-bubble-wrapper {
      align-items: flex-end !important;
    }

    .message-row.user .message-bubble-wrapper {
      align-items: flex-start !important;
    }

    .message-bubble {
      padding: 12px 18px !important;
      font-size: 14px !important;
      line-height: 1.5 !important;
      position: relative !important;
      word-break: break-word !important;
      overflow-wrap: break-word !important;
      word-wrap: break-word !important;
    }

    .message-row.admin .message-bubble {
      background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
      color: #ffffff !important;
      border-radius: 16px 16px 4px 16px !important;
      box-shadow: 0 4px 15px rgba(37, 99, 235, 0.15) !important;
    }

    .message-row.user .message-bubble {
      background: #ffffff !important;
      color: #1e293b !important;
      border: 1px solid #e2e8f0 !important;
      border-radius: 16px 16px 16px 4px !important;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02) !important;
    }

    .message-bubble a {
      color: inherit !important;
      text-decoration: underline !important;
      font-weight: 500 !important;
      word-break: break-all !important;
      transition: opacity 0.2s ease !important;
    }
    .message-bubble a:hover {
      opacity: 0.85 !important;
    }
    .message-row.user .message-bubble a {
      color: #2563eb !important;
    }

    .message-meta {
      display: flex !important;
      flex-direction: column !important;
      align-items: flex-end !important;
      gap: 3px !important;
      margin-top: 6px !important;
      font-size: 11px !important;
      color: #94a3b8 !important;
    }

    .message-row.user .message-meta {
      align-items: flex-start !important;
    }

    .message-meta-row {
      display: flex !important;
      align-items: center !important;
      gap: 6px !important;
    }

    .message-sender-label {
      font-size: 10px !important;
      color: #94a3b8 !important;
      font-style: italic !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      max-width: 200px !important;
    }

    .read-status {
      display: flex !important;
      align-items: center !important;
    }

    .read-status.read {
      color: #3b82f6 !important;
    }

    .date-separator-wrapper {
      display: flex !important;
      justify-content: center !important;
      align-items: center !important;
      margin: 16px 0 !important;
      position: relative !important;
    }

    .date-separator-wrapper::before {
      content: '' !important;
      position: absolute !important;
      left: 0 !important;
      right: 0 !important;
      height: 1px !important;
      background: #e2e8f0 !important;
      z-index: 1 !important;
    }

    .date-separator {
      background: #e2e8f0 !important;
      color: #475569 !important;
      font-size: 11px !important;
      font-weight: 600 !important;
      padding: 4px 12px !important;
      border-radius: 12px !important;
      position: relative !important;
      z-index: 2 !important;
      text-transform: uppercase !important;
      letter-spacing: 0.5px !important;
    }

    .chat-input-area {
      display: flex !important;
      align-items: center !important;
      padding: 16px 24px !important;
      background: #ffffff !important;
      border-top: 1px solid #e2e8f0 !important;
      gap: 12px !important;
    }

    .chat-input-wrapper {
      flex: 1 !important;
      display: flex !important;
      align-items: center !important;
      background: #f8fafc !important;
      border: 1px solid #e2e8f0 !important;
      border-radius: 24px !important;
      padding: 6px 16px !important;
      transition: all 0.2s ease !important;
    }

    .chat-input-wrapper:focus-within {
      background: #ffffff !important;
      border-color: #3b82f6 !important;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
    }

    .chat-input-field {
      flex: 1 !important;
      border: none !important;
      background: transparent !important;
      outline: none !important;
      padding: 8px 4px !important;
      font-size: 14px !important;
      color: #0f172a !important;
      resize: none !important;
      height: 36px;
      max-height: 76px !important;
      overflow-y: hidden;
      line-height: 1.4 !important;
    }

    .send-btn {
      width: 44px !important;
      height: 44px !important;
      border-radius: 50% !important;
      background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
      border: none !important;
      color: #ffffff !important;
      cursor: pointer !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2) !important;
      transition: all 0.2s ease !important;
      flex-shrink: 0 !important;
    }

    .send-btn:hover:not(:disabled) {
      transform: scale(1.05) !important;
      box-shadow: 0 6px 16px rgba(37, 99, 235, 0.3) !important;
    }

    .send-btn:disabled {
      background: #cbd5e1 !important;
      box-shadow: none !important;
      cursor: not-allowed !important;
    }

    .chat-loader {
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      justify-content: center !important;
      padding: 40px 0 !important;
      gap: 12px !important;
    }

    .spinner {
      animation: spin 1s linear infinite !important;
      color: #3b82f6 !important;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .message-bubble.media-bubble {
      background: transparent !important;
      box-shadow: none !important;
      border: none !important;
      padding: 0 !important;
    }

    .pdf-message-card {
      display: flex !important;
      align-items: center !important;
      gap: 12px !important;
      padding: 10px 14px !important;
      background: #ffffff !important;
      border: 1px solid #e2e8f0 !important;
      border-radius: 12px !important;
      max-width: 280px !important;
      width: 100% !important;
      box-sizing: border-box !important;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03) !important;
    }
    
    .message-row.admin .pdf-message-card {
      background: #1e293b !important;
      border-color: #334155 !important;
    }
    
    .pdf-icon-wrapper {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 40px !important;
      height: 40px !important;
      border-radius: 8px !important;
      background: #fef2f2 !important;
      color: #ef4444 !important;
      flex-shrink: 0 !important;
    }
    
    .message-row.admin .pdf-icon-wrapper {
      background: #7f1d1d !important;
      color: #fca5a5 !important;
    }
    
    .pdf-details {
      flex: 1 !important;
      display: flex !important;
      flex-direction: column !important;
      min-width: 0 !important;
    }
    
    .pdf-name {
      font-size: 13px !important;
      font-weight: 600 !important;
      color: #0f172a !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }
    
    .message-row.admin .pdf-name {
      color: #f8fafc !important;
    }
    
    .pdf-type {
      font-size: 11px !important;
      color: #64748b !important;
      margin-top: 2px !important;
    }
    
    .message-row.admin .pdf-type {
      color: #94a3b8 !important;
    }
    
    .pdf-download-btn, .image-download-btn {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 32px !important;
      height: 32px !important;
      border-radius: 50% !important;
      background: #f1f5f9 !important;
      color: #475569 !important;
      cursor: pointer !important;
      transition: all 0.2s ease !important;
      text-decoration: none !important;
    }
    
    .message-row.admin .pdf-download-btn, .message-row.admin .image-download-btn {
      background: #334155 !important;
      color: #cbd5e1 !important;
    }
    
    .pdf-download-btn:hover, .image-download-btn:hover {
      background: #e2e8f0 !important;
      color: #0f172a !important;
    }
    
    .message-row.admin .pdf-download-btn:hover, .message-row.admin .image-download-btn:hover {
      background: #475569 !important;
      color: #ffffff !important;
    }

    .image-message-card {
      position: relative !important;
      max-width: 240px !important;
      width: 100% !important;
      box-sizing: border-box !important;
      border-radius: 12px !important;
      overflow: hidden !important;
      border: 1px solid #e2e8f0 !important;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05) !important;
      background: #ffffff !important;
      display: flex !important;
      flex-direction: column !important;
    }
    
    .message-row.admin .image-message-card {
      border-color: #334155 !important;
      background: #1e293b !important;
    }

    .chat-image-preview {
      width: 100% !important;
      height: auto !important;
      max-height: 180px !important;
      object-fit: cover !important;
      display: block !important;
    }

    .image-footer {
      display: flex !important;
      align-items: center !important;
      justify-content: space-between !important;
      padding: 8px 12px !important;
      background: #f8fafc !important;
      gap: 8px !important;
    }
    
    .message-row.admin .image-footer {
      background: #0f172a !important;
    }

    .image-name {
      font-size: 12px !important;
      font-weight: 500 !important;
      color: #334155 !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      flex: 1 !important;
    }
    
    .message-row.admin .image-name {
      color: #cbd5e1 !important;
    }
  `;

  return (
    <div className="admin-chat-layout">
      <style dangerouslySetInnerHTML={{ __html: cssStyles }} />
      
      {/* Header */}
      <div className="chat-header" style={{flexWrap:window.innerWidth <= 1200 ? "wrap" : ""}}>
        <div className="chat-header-info">
          <div className="chat-header-avatar">
            {customerName.charAt(0).toUpperCase()}
          </div>
          <div className="chat-header-details">
            <h4>{customerName}</h4>
            <p>
              ID: {customerAppId || 'N/A'} | Mobile: {customerMobile || 'N/A'} | Email: {customerEmail || 'N/A'}
            </p>
          </div>
        </div>
        
        <div className="chat-header-status">
          {connectionStatus === 'connecting' ? (
            <RefreshCw className="spinner" size={12} style={{ marginRight: '8px' }} />
          ) 
          : (
            <span className={`status-dot ${connectionStatus}`} />
          )}
          {connectionStatus === 'connected' && 'Connected'}
          {connectionStatus === 'connecting' && 'Connecting...'}
          {connectionStatus === 'disconnected' && 'Disconnected'}
        </div>
      </div>

      {/* Search Bar */}
      <div className="chat-search-bar">
        <Search size={14} />
        <input
          type="text"
          placeholder="Search messages..."
          className="chat-search-input"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Message Feed */}
      <div className="chat-messages-area">
        {loading ? (
          <div className="chat-loader">
            <RefreshCw className="spinner" size={28} />
            <p style={{ fontSize: '13px',textAlign:'center',color:"#94a3b8" }}>Loading conversation history...</p>
          </div>
        ) : filteredMessages.length === 0 ? (
          <div className="chat-empty-state">
            <MessageSquare size={36} style={{ color: '#94a3b8' }} />
            <h5 style={{ margin: '8px 0 2px 0', fontSize: '14px', fontWeight: '600' ,textAlign:'center',color:"#94a3b8"}}>No messages found</h5>
            <p style={{ margin: 0, fontSize: '12px',textAlign:'center',color:"#94a3b8" }}>
              {searchQuery ? 'Try matching another search query' : 'Type a reply below to start the conversation'}
            </p>
          </div>
        ) : (
          filteredMessages.map((msg, index) => {
            const isAdmin = msg.senderType === 'ADMIN';
            const formattedTime = moment(msg.addedOn).format('hh:mm A');
            const formattedDate = moment(msg.addedOn).format('DD MMM YYYY');
            
            const showDateSeparator = index === 0 || 
              moment(msg.addedOn).format('YYYY-MM-DD') !== moment(filteredMessages[index - 1].addedOn).format('YYYY-MM-DD');

            return (
              <React.Fragment key={msg.messageId || index}>
                {showDateSeparator && (
                  <div className="date-separator-wrapper">
                    <span className="date-separator">{formattedDate}</span>
                  </div>
                )}
                <div className={`message-row ${isAdmin ? 'admin' : 'user'}`}>
                  <div className="message-bubble-wrapper">
                    <div className={`message-bubble ${typeof msg.comment === 'string' && msg.comment.includes('downloadpdf') ? 'media-bubble' : ''}`}>
                      {renderMessageContent(msg.comment)}
                    </div>
                    <div className="message-meta">
                      <div className="message-meta-row">
                        <span>{formattedTime}</span>
                        {isAdmin && (
                          <span className={`read-status ${(msg.isRead || msg.read) ? 'read' : ''}`}>
                            {(msg.isRead || msg.read) ? <CheckCheck size={12} /> : <Check size={12} />}
                          </span>
                        )}
                      </div>
                      {isAdmin && msg.senderId && (
                        <span className="message-sender-label" title={msg.senderId}>
                          {msg.senderId}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="chat-input-area">
        <div className="chat-input-wrapper">
          <textarea
            ref={textareaRef}
            className="chat-input-field"
            placeholder={connectionStatus === 'connected' ? "Type your message here..." : "Connecting to chat server..."}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            disabled={connectionStatus !== 'connected'}
            style={{scrollbarWidth:"thin"}}
          />
        </div>
        <button
          type="button"
          className="send-btn"
          onClick={() => sendMessage(inputValue)}
          disabled={!inputValue.trim() || sending || connectionStatus !== 'connected'}
        >
          <Send size={15} />
        </button>
      </div>
    </div>
  );
}
